import { lazy } from "react";
import { useRoutes } from "react-router-dom";
import NotFound from "@/pages/not-found";

// Every route is split into its own chunk. The admin console in particular is
// large and is never downloaded by an ordinary participant.
const Home = lazy(() => import("@/pages/home"));
const Signin = lazy(() => import("@/pages/signin"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const CommunityFeed = lazy(() => import("@/pages/community-feed"));
const Feedback = lazy(() => import("@/pages/feedback"));
const Badges = lazy(() => import("@/pages/badges"));
const Exercises = lazy(() => import("@/pages/exercises"));
const Integrations = lazy(() => import("@/pages/integrations"));
const Kudos = lazy(() => import("@/pages/kudos"));
const Memories = lazy(() => import("@/pages/memories"));
const Notifications = lazy(() => import("@/pages/notifications"));
const Offline = lazy(() => import("@/pages/offline"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Terms = lazy(() => import("@/pages/terms"));
const Support = lazy(() => import("@/pages/support"));
const Accessibility = lazy(() => import("@/pages/accessibility"));
const Profile = lazy(() => import("@/pages/profile"));
const ParticipantProfile = lazy(() => import("@/pages/participant-profile"));
const Workspaces = lazy(() => import("@/pages/workspaces"));

const ChallengeList = lazy(() => import("@/pages/challenges/challenge-list"));
const ChallengeDetail = lazy(() => import("@/pages/challenges/challenge-detail"));
const ChallengeLeaderboard = lazy(
  () => import("@/pages/challenges/challenge-leaderboard"),
);
const LeaderboardIndex = lazy(() => import("@/pages/challenges/leaderboard-index"));
const ChallengeRegister = lazy(() => import("@/pages/challenges/challenge-register"));
const ChallengeTeams = lazy(() => import("@/pages/challenges/challenge-teams"));
const ChallengeTracker = lazy(() => import("@/pages/challenges/challenge-tracker"));
const ChallengeCompletion = lazy(
  () => import("@/pages/challenges/challenge-completion"),
);

const AdminHub = lazy(() => import("@/pages/admin/admin-hub"));
const AdminHome = lazy(() => import("@/pages/admin/admin-home"));
const AdminAccessRequests = lazy(() => import("@/pages/admin/access-requests"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/announcements"));
const AdminAudit = lazy(() => import("@/pages/admin/audit"));
const AdminArchivedMemories = lazy(() => import("@/pages/admin/archived-memories"));
const AdminMemoryImport = lazy(() => import("@/pages/admin/memory-import"));
const AdminChallengeList = lazy(() => import("@/pages/admin/challenge-list"));
const AdminChallengeNew = lazy(() => import("@/pages/admin/challenge-new"));
const AdminChallengeEdit = lazy(() => import("@/pages/admin/challenge-edit"));
const AdminThemes = lazy(() => import("@/pages/admin/themes"));
const AdminUsers = lazy(() => import("@/pages/admin/users"));
const AdminFeedback = lazy(() => import("@/pages/admin/feedback"));
const AdminWhatsApp = lazy(() => import("@/pages/admin/whatsapp"));
const AdminOperationsReport = lazy(() => import("@/pages/admin/operations-report"));


const WorkspaceAdminHome = lazy(
  () => import("@/pages/workspace-admin/workspace-admin-home"),
);
const WorkspaceAdminEmails = lazy(() => import("@/pages/workspace-admin/emails"));

/** Mirrors the URLs the Next.js app router previously served, one for one. */
export function AppRoutes() {
  return useRoutes([
    { path: "/", element: <Home /> },
    { path: "/signin", element: <Signin /> },
    { path: "/dashboard", element: <Dashboard /> },
    { path: "/community", element: <CommunityFeed /> },
    { path: "/feedback", element: <Feedback /> },
    { path: "/badges", element: <Badges /> },
    { path: "/exercises", element: <Exercises /> },
    { path: "/integrations", element: <Integrations /> },
    { path: "/kudos", element: <Kudos /> },
    { path: "/memories", element: <Memories /> },
    { path: "/notifications", element: <Notifications /> },
    { path: "/offline", element: <Offline /> },
    { path: "/privacy", element: <Privacy /> },
    { path: "/terms", element: <Terms /> },
    { path: "/support", element: <Support /> },
    { path: "/accessibility", element: <Accessibility /> },
    { path: "/profile", element: <Profile /> },
    { path: "/participants/:participantUserId", element: <ParticipantProfile /> },
    { path: "/workspaces", element: <Workspaces /> },

    { path: "/challenges", element: <ChallengeList /> },
    { path: "/leaderboard", element: <LeaderboardIndex /> },
    { path: "/challenges/:slug", element: <ChallengeDetail /> },
    { path: "/challenges/:slug/leaderboard", element: <ChallengeLeaderboard /> },
    { path: "/challenges/:slug/register", element: <ChallengeRegister /> },
    { path: "/challenges/:slug/teams", element: <ChallengeTeams /> },
    { path: "/challenges/:slug/tracker", element: <ChallengeTracker /> },
    { path: "/challenges/:slug/completion", element: <ChallengeCompletion /> },

    { path: "/admin", element: <AdminHub /> },
    { path: "/admin/health", element: <AdminHome /> },
    { path: "/admin/access-requests", element: <AdminAccessRequests /> },
    { path: "/admin/announcements", element: <AdminAnnouncements /> },
    { path: "/admin/audit", element: <AdminAudit /> },
    { path: "/admin/memories", element: <AdminArchivedMemories /> },
    { path: "/admin/memories/import", element: <AdminMemoryImport /> },
    { path: "/admin/challenges", element: <AdminChallengeList /> },
    { path: "/admin/challenges/new", element: <AdminChallengeNew /> },
    { path: "/admin/challenges/:challengeId", element: <AdminChallengeEdit /> },
    { path: "/admin/themes", element: <AdminThemes /> },
    { path: "/admin/users", element: <AdminUsers /> },
    { path: "/admin/feedback", element: <AdminFeedback /> },
    { path: "/admin/whatsapp", element: <AdminWhatsApp /> },
    { path: "/admin/reports/operations", element: <AdminOperationsReport /> },


    { path: "/workspace-admin", element: <WorkspaceAdminHome /> },
    { path: "/workspace-admin/emails", element: <WorkspaceAdminEmails /> },

    { path: "*", element: <NotFound /> },
  ]);
}
